import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import Select from 'react-select';
/* components */
import FormHeader from '../../components/FormHeader';
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import ModalRemoveItem from '../../components/ModalRemoveItem';
/* actions */
import { getAllDevices } from '../../redux/actions/devices';
import {
  getAllRecordPorts,
  editRecordPortsAnalogic,
  removeAnalogicRecordPorts
} from '../../redux/actions/recordPorts';
/* form-validation */
import schema from '../../form-validation/analogicRecorder';
import portsVoicePanel from './portsVoicePanel';

function Analogic () {
  const sortable = (a, b) => {
    const n1 = Number(a.label.split(' | ')[0], 10);
    const n2 = Number(b.label.split(' | ')[0], 10);
    return n1 < n2 ? -1 : n1 > n2 ? 1 : 0;
  };

  const customStyle = {
    control: provided => ({
      ...provided,
      borderRadius: '0px',
      '&:hover': {
        color: '#495057',
        backgroundColor: '#fff',
        borderColor: '#80bdff',
        outline: 0,
        boxShadow: '0 0 0 .2rem rgba(0,123,255,.25)'
      }
    }),
    menu: provided => ({ ...provided, marginTop: '1px' }),
    downchevron: provided => ({ ...provided, borderRadius: '100px' })
  };

  const customThemes = theme => ({
    ...theme,
    colors: { ...theme.colors, primary: '#80bdff' }
  });

  const customFilter = (option, searchText) =>
    !!option.data.label.toLowerCase().includes(searchText.toLowerCase());

  const INITIAL_VALUES = {
    voicePanel: '',
    ebs: '',
    ebsports: [],
    deviceIds: [],
    devices: []
  };

  const { register, handleSubmit, errors } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const submitButton = useRef();
  const dispatch = useDispatch();
  const recordPorts = useSelector(state => state.recordPorts);
  const devices = useSelector(state => state.devices.data);
  const [data, setData] = useState({ ...INITIAL_VALUES });
  const [firstOption] = useState({
    value: '',
    label: '-- Selecionar dispositivo --'
  });
  const [deviceOptions, setDeviceOptions] = useState([firstOption]);
  const [enableButtonRemoveConfig, setEnableButtonRemoveConfig] = useState(
    false
  );
  const [showModal, setShowModal] = useState(false);
  const handleModalClose = () => setShowModal(false);
  const handleModalShow = () => setShowModal(true);
  const handleModalConfirmYes = () => {
    const formReset = () => {
      const { voicePanel } = data;
      setData({ ...INITIAL_VALUES, voicePanel });
    };
    dispatch(removeAnalogicRecordPorts(data, formReset, submitButton.current));
    setShowModal(false);
  };
  const [titleModal] = useState('Confirmação');
  const [bodyModal] = useState('Deseja excluir as portas de gravação ');
  const [nameItemWillRemove, setNameItemWillRemove] = useState('');
  const removeAnalogicRecordConfig = () => {
    const [start, end] = data.voicePanel.split('_');
    const msg = `${start} à ${end}`;
    setNameItemWillRemove(msg);
    handleModalShow();
  };

  useEffect(() => {
    dispatch(getAllDevices());
    dispatch(getAllRecordPorts());
  }, [dispatch]);

  useEffect(() => {
    if (recordPorts.editSuccess) {
      setEnableButtonRemoveConfig(true);
    }
  }, [recordPorts.editSuccess]);

  useEffect(() => {
    if (recordPorts.removeSuccess) {
      setEnableButtonRemoveConfig(false);
    }
  }, [recordPorts.removeSuccess]);

  useEffect(() => {
    if (devices.length) {
      const extensions = devices.filter(device => device.type === 'E');

      const options = extensions
        .map(device => ({
          number: `${device.number}`,
          id: `${device._id}`,
          value: `${device._id}`,
          label: `${device.number} | Dpto: ${device.department.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setDeviceOptions([...options]);
    }
  }, [devices, setDeviceOptions, firstOption]);

  const submit = () => {
    dispatch(editRecordPortsAnalogic(data, submitButton.current));
  };

  const handleChange = evt => {
    evt.preventDefault();
    if (evt.target.getAttribute('number') === 'on') {
      if (evt.target.value.match(/^\d+$/) || evt.target.value === '') {
        setData({
          ...data,
          [evt.target.name]: evt.target.value
        });
      } else {
        evt.target.value = data[evt.target.name];
      }
    } else {
      const isCheckbox = evt.target.type === 'checkbox';
      setData({
        ...data,
        [evt.target.name]: isCheckbox ? evt.target.checked : evt.target.value
      });
    }
  };

  const handleChangePortsVoidPanel = evt => {
    setEnableButtonRemoveConfig(false);
    const { name, value } = evt.target;
    const recports = recordPorts.data.filter(
      recordPort => recordPort.voicePanel === value
    );
    if (recports.length) {
      setEnableButtonRemoveConfig(true);
    }
    setData({
      ...data,
      [name]: value,
      ebs: recports[0] ? recports[0].ebs : '',
      ebsports: recports.map(recport => recport.ebsport),
      deviceIds: recports.map(recport => recport.deviceId),
      devices: recports.map(recport => recport.device)
    });
  };

  const handleChangeSelect = field => {
    const { value, name } = field;
    const port = name.split('_')[1];
    const { ebsports, devices, deviceIds } = data;
    const position = data.ebsports.findIndex(
      ebsport => parseInt(ebsport) === parseInt(port)
    );

    if (position >= 0) {
      ebsports.splice(position, 1);
      deviceIds.splice(position, 1);
      devices.splice(position, 1);
    }

    if (value) {
      const { number, id } = field;
      ebsports.push(port);
      deviceIds.push(id);
      devices.push(number);
    }

    setData({ ...data, ebsports, deviceIds, devices });
  };

  const row = port => {
    return (
      <div className="row" key={port}>
        <div className="col-sm-6 col-md-3 col-lg-2 m-auto">
          <div className="form-group">
            <span>
              {`${
                port >= 9 ? port + 1 : String.fromCharCode(160) + (port + 1)
              }`}
              &deg; Porta EBS
            </span>
          </div>
        </div>
        <div className="col-sm-6 col-md-9 col-lg-10 m-auto">
          <div className="form-group">
            <Select
              name={`device_${port}`}
              className="devices"
              ref={register}
              onChange={evt =>
                handleChangeSelect({ ...evt, name: `device_${port}` })
              }
              value={
                data.ebsports.map(port => String(port)).includes(String(port))
                  ? deviceOptions.find(opt => {
                    const position = data.ebsports.findIndex(
                      ebsport => String(ebsport) === String(port)
                    );
                    return data.deviceIds[position] === opt.value;
                  })
                  : deviceOptions[0]
              }
              options={deviceOptions}
              isSearchable={true}
              noOptionsMessage={() => 'Nenhum registro encontrado'}
              theme={customThemes}
              styles={customStyle}
              filterOption={customFilter}
              isDisabled={!data.voicePanel}
            />
            {errors[`device_${port}`] &&
              errors[`device_${port}`].type === 'repeat' && (
              <ErrorMsgField type="repeat" />
            )}
            {errors[`device_${port}`] &&
              errors[`device_${port}`].type === 'some' && (
              <ErrorMsgField type="some" />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid">
      <ModalRemoveItem
        showModal={showModal}
        handleModalClose={handleModalClose}
        handleModalConfirmYes={handleModalConfirmYes}
        titleModal={titleModal}
        bodyModal={bodyModal}
        nameItemWillRemove={nameItemWillRemove}
      />
      {recordPorts.removeSuccess && (
        <AlertMsg type="success" msg="Configuração removidas com sucesso" />
      )}
      {!recordPorts.editSuccess && recordPorts.removeError && (
        <AlertMsg type="danger" msg="Falha ao remover configuração" />
      )}
      {recordPorts.editSuccess && (
        <AlertMsg
          type="success"
          msg="Portas de gravação atualizada com sucesso"
        />
      )}
      {recordPorts.errorContent &&
        !recordPorts.errorContent.message.includes('400') &&
        recordPorts.editError && (
        <AlertMsg
          type="danger"
          msg="Falha durante atualização das portas de gravação"
        />
      )}
      {recordPorts.errorContent &&
        recordPorts.errorContent.message.includes('400') &&
        recordPorts.editError && (
        <AlertMsg
          type="danger"
          msg="O EBS informado já está cadastrado em outro Voice Panel"
        />
      )}
      {!recordPorts.editSuccess &&
        !recordPorts.editError &&
        !recordPorts.removeSuccess &&
        !recordPorts.removeError && (
        <AlertMsg type="info" msg="Área configuração portas de gravação" />
      )}
      <FormHeader title="Gravação Analógica" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="form-row align-items-center">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="voicePanel">
                Porta Voice Panel<span className="field-required">*</span>
              </label>
              <select
                className="form-control"
                id="voicePanel"
                name="voicePanel"
                onChange={handleChangePortsVoidPanel}
                ref={register}
                value={data.voicePanel}
              >
                <option value="">-- Selecione --</option>
                {portsVoicePanel.map(port => {
                  const { label, value } = port;
                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  );
                })}
              </select>
              {errors.voicePanel && errors.voicePanel.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.voicePanel && errors.voicePanel.type === 'typeError' && (
                <ErrorMsgField type="required" />
              )}
              {!errors.voicePanel && (
                <small className="form-text m-0">&nbsp;</small>
              )}
            </div>
          </div>
          <div className="col-sm-5">
            <div className="form-group">
              <label htmlFor="ebs">
                ID EBS<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="ebs"
                name="ebs"
                number="on"
                autoComplete="off"
                onChange={handleChange}
                ref={register}
                value={data.ebs}
                disabled={!data.voicePanel}
              />
              {errors.ebs && errors.ebs.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.ebs && errors.ebs.type === 'typeError' && (
                <ErrorMsgField type="required" />
              )}
              {!errors.ebs && <small className="form-text m-0">&nbsp;</small>}
            </div>
          </div>
          <div className="col-sm-1">
            <div className="form-group">
              <button
                type="button"
                title="Remover configurações"
                className="btn btn-danger btn-block btn-remove-recess rounded-0"
                onClick={removeAnalogicRecordConfig}
                disabled={!enableButtonRemoveConfig}
              >
                <i className="fa fa-trash-alt"></i>
              </button>
              <small className="form-text m-0">&nbsp;</small>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            {[...Array(12)].map((port, i) => row(i))}
          </div>
          <div className="col-sm-6">
            {[...Array(12)].map((port, i) => row(i + 12))}
          </div>
        </div>
        <div className="row">
          <div className="container-fluid">
            <button
              type="submit"
              ref={submitButton}
              className="btn btn-primary float-right"
            >
              Salvar
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default Analogic;
